// components/Hero.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Array of hero images and their corresponding texts
const heroData = [
  {
    image: 'hero-bg.jpg',
    title: 'Welcome to National Parents Association',
    subtitle: "Empowering parents to be active participants in their children's education"
  },
  {
    image: 'hero-bg2.jpg', // Add your additional images
    title: 'Building Strong Parent Communities',
    subtitle: 'Together we can make a difference in education'
  },
  {
    image: 'hero-bg3.jpg',
    title: 'Join Our Growing Network',
    subtitle: 'Connect with parents and educators nationwide'
  }
];

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const HeroSection = styled.section`
  position: relative;
  height: calc(100vh - 120px);
  overflow: hidden;
`;

const HeroSlide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url(${props => props.image});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  opacity: ${props => (props.active ? 1 : 0)};
  transition: opacity 0.8s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  padding: 2rem;
  z-index: 2;
  text-align: center;
  color: white;
  animation: ${fadeIn} 1s ease-in-out;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAButton = styled(Link)`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const SlideButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 1rem;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  ${props => props.direction === 'left' ? 'left: 2rem;' : 'right: 2rem;'}

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0.8rem;
    ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  }
`;

const SlideIndicators = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
  z-index: 3;
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  background: ${props => props.active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
  }
`;

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroData.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % heroData.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + heroData.length) % heroData.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  return (
    <HeroSection>
      {heroData.map((slide, index) => (
        <HeroSlide
          key={index}
          image={slide.image}
          active={index === currentSlide}
        >
          {index === currentSlide && (
            <HeroContent>
              <Title>{slide.title}</Title>
              <Subtitle>{slide.subtitle}</Subtitle>
              <CTAButton to="/membership">Join Us Today</CTAButton>
            </HeroContent>
          )}
        </HeroSlide>
      ))}

      <SlideButton direction="left" onClick={prevSlide}>
        <FaChevronLeft />
      </SlideButton>
      <SlideButton direction="right" onClick={nextSlide}>
        <FaChevronRight />
      </SlideButton>

      <SlideIndicators>
        {heroData.map((_, index) => (
          <Indicator
            key={index}
            active={index === currentSlide}
            onClick={() => goToSlide(index)}
          />
        ))}
      </SlideIndicators>
    </HeroSection>
  );
};

export default Hero;